import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { ITicket } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { currencies } from '../CurrencyTextInput/CurrencyTextInput';

interface ITicketCardProps {
    ticket: ITicket;
    onDuplicate?: (ticket: ITicket) => void;
    onDelete?: (ticket: ITicket) => void;
    onEdit?: (ticket: ITicket) => void;
}

const TicketCard: React.FC<ITicketCardProps> = ({ ticket, onDelete, onDuplicate, onEdit }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { themeStyle } = useThemeContext();
    const open = Boolean(anchorEl);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <Box
            sx={{
                width: 'fit-content',
                height: 80,
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '16px',
                position: 'relative',
            }}>
            {/* Ticket Icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConfirmationNumberIcon
                    fontSize="small"
                    sx={{
                        color: theme[themeStyle].tag_white,
                    }}
                />
                <Typography
                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: theme[themeStyle].tag_white }}>
                    {ticket.fee_name}
                </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ marginTop: 0.5, display: 'flex' }}>
                <Typography
                    style={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: theme[themeStyle].tag_white }}>
                    {ticket.displayCurrencyValue ??
                        `${currencies.find(cur => cur.value === ticket.currency)?.label} ${ticket.fee}`}
                </Typography>
            </Box>

            {/* Edit Icon */}
            <IconButton
                sx={{
                    position: 'absolute',
                    top: -15,
                    right: -15,
                    fontSize: 8,
                    backgroundColor: theme[themeStyle].deep_grey,

                    borderRadius: '50%',
                }}
                size="small"
                onClick={handleMenuClick}>
                <EditIcon sx={{ color: theme[themeStyle].tag_white }} />
            </IconButton>

            {/* ClickAwayListener to handle click-away */}
            <Menu
                PaperProps={{
                    sx: {
                        bgcolor: theme[themeStyle].gray_text,
                        color: theme[themeStyle].tag_white,
                        marginTop: 0,
                        '& .MuiMenuItem-root': {
                            padding: 2,
                        },
                    },
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}>
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        onEdit?.(ticket);
                    }}>
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        onDuplicate?.(ticket);
                    }}>
                    Duplicate Ticket
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        onDelete?.(ticket);
                    }}>
                    Delete
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default TicketCard;
